import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, AlertTriangle, Download } from "lucide-react";
import { confirm } from "@/lib/confirm";
import { fmtDateTime, pendingDiscrepancyCount } from "./utils";

function severityTone(sev) {
  if (sev === "fill") return "bg-blue-100 text-blue-800";
  if (sev === "high") return "bg-red-100 text-red-800";
  return "bg-amber-100 text-amber-800";
}
function severityLabel(sev) {
  if (sev === "fill") return "Preencher";
  if (sev === "high") return "Divergência";
  if (sev === "low") return "Parcial";
  return sev;
}

/** Tab "Divergências" — list of AI analyses with apply/dismiss actions. */
export default function DivergenciasTab({ analyses, onResolve, onDeleteAnalysis }) {
  const pending = pendingDiscrepancyCount(analyses);
  const resolved = analyses.reduce(
    (s, a) => s + (a.discrepancies || []).filter((d) => d.resolved_at).length,
    0
  );

  return (
    <Card><CardContent className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-700" /> Análises por IA
        </h3>
        <div className="text-xs text-slate-500">
          {analyses.length} análise(s) · {pending} pendente(s) · {resolved} resolvida(s)
        </div>
      </div>
      {analyses.length === 0 && (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-6 text-center">
          Nenhuma análise feita ainda. Envie um documento assinado na aba <strong>Assinados</strong> e clique em <strong>Analisar com IA</strong>.
        </div>
      )}
      {analyses.map((a) => (
        <AnalysisCard key={a.id} a={a} onResolve={onResolve} onDeleteAnalysis={onDeleteAnalysis} />
      ))}
    </CardContent></Card>
  );
}

function AnalysisCard({ a, onResolve, onDeleteAnalysis }) {
  const discrepancies = a.discrepancies || [];
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={`analysis-${a.id}`}>
      <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 border-b border-slate-200 gap-2 flex-wrap">
        <div className="text-sm min-w-0">
          <div className="font-medium text-slate-900 truncate">{a.doc_label || a.doc_type}</div>
          <div className="text-xs text-slate-500">{fmtDateTime(a.analyzed_at)} · {a.analyzed_by_name} · {a.model}</div>
        </div>
        <div className="flex items-center gap-2">
          {discrepancies.length === 0 ? (
            <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-medium">✓ sem divergências</span>
          ) : (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {discrepancies.length} divergência(s)
            </span>
          )}
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDeleteAnalysis(a.id)}>
            <Download className="h-3.5 w-3.5 rotate-180" />
          </Button>
        </div>
      </div>
      {discrepancies.length > 0 && (
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-1.5 font-medium">Campo</th>
                <th className="text-left py-1.5 font-medium">No sistema</th>
                <th className="text-left py-1.5 font-medium">No documento</th>
                <th className="text-left py-1.5 font-medium">Tipo</th>
                <th className="text-right py-1.5 font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              {discrepancies.map((d) => (
                <DiscrepancyRow key={d.field} analysisId={a.id} d={d} onResolve={onResolve} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {a.extracted && (
        <details className="border-t border-slate-200">
          <summary className="text-xs text-slate-500 px-4 py-2 cursor-pointer hover:bg-slate-50">Dados extraídos pela IA</summary>
          <pre className="text-[11px] font-mono bg-slate-50 p-3 overflow-auto max-h-60">{JSON.stringify(a.extracted, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function DiscrepancyRow({ analysisId, d, onResolve }) {
  const resolved = !!d.resolved_at;
  const sev = d.severity || "high";

  async function applyWithConfirm() {
    if (!(await confirm({ description: `Substituir "${d.expected ?? "—"}" por "${d.found ?? "—"}"?` }))) return;
    onResolve(analysisId, d.field, "apply");
  }

  return (
    <tr className={`border-t border-slate-100 ${resolved ? "opacity-50" : ""}`}>
      <td className="py-1.5 font-medium text-slate-700">{d.label || d.field}</td>
      <td className="py-1.5 text-slate-600">{String(d.expected ?? "—")}</td>
      <td className="py-1.5 text-slate-600">{String(d.found ?? "—")}</td>
      <td className="py-1.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${severityTone(sev)}`}>
          {severityLabel(sev)}
        </span>
      </td>
      <td className="py-1.5 text-right">
        {resolved ? (
          <span className="text-[10px] text-slate-500 italic">
            {d.resolution === "apply" ? "aplicado" : "descartado"} por {d.resolved_by || "—"}
          </span>
        ) : d.can_auto_apply === false ? (
          <span className="text-[10px] text-slate-500 italic">editar no cadastro manualmente</span>
        ) : sev === "fill" ? (
          <div className="inline-flex gap-1.5">
            <Button size="sm" className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800" onClick={() => onResolve(analysisId, d.field, "apply")} data-testid={`btn-apply-${d.field}`}>Preencher</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResolve(analysisId, d.field, "dismiss")} data-testid={`btn-dismiss-${d.field}`}>Ignorar</Button>
          </div>
        ) : (
          <div className="inline-flex gap-1.5">
            <Button size="sm" className="h-7 text-xs bg-blue-700 hover:bg-blue-800" onClick={applyWithConfirm} data-testid={`btn-apply-${d.field}`}>
              Aplicar do documento
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResolve(analysisId, d.field, "dismiss")} data-testid={`btn-keep-${d.field}`}>
              Manter sistema
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}
